/** Human labels and settlement review rows for Accounts page. */

export function isTruckLoadContract(contract) {
  return String(contract?.delivery_condition || "").toUpperCase() === "TRUCK_LOAD";
}

export function isForDeliveryContract(contract) {
  return String(contract?.delivery_condition || "").toUpperCase() === "FOR";
}

export function requiresFreightSettlementContract(contract) {
  return isTruckLoadContract(contract) || isForDeliveryContract(contract);
}

export function isMandiTruckLoadContract(contract) {
  return contract?.purchase_type === "MANDI_PURCHASE" && isTruckLoadContract(contract);
}

/** Truck load or FOR — separate freight voucher (accounts may waive). */
export function purchaseNeedsFreightVoucher(purchase) {
  if (purchase?.needs_freight_voucher) return true;
  return purchaseRequiresFreightSettlement(purchase);
}

export function purchaseRequiresFreightSettlement(purchase) {
  const allocs = purchase?.contract_allocations || [];
  return allocs.some((a) => requiresFreightSettlementContract(a));
}

export function defaultFreightRateFromPurchase(purchase) {
  const allocs = purchase?.contract_allocations || [];
  for (const a of allocs) {
    if (requiresFreightSettlementContract(a) && a.freight_rate_per_qtl != null && a.freight_rate_per_qtl !== "") {
      return a.freight_rate_per_qtl;
    }
  }
  if (purchase?.freight_rate_per_qtl != null && purchase.freight_rate_per_qtl !== "") {
    return purchase.freight_rate_per_qtl;
  }
  return "";
}

export function purchaseHasTruckLoadContract(purchase) {
  const allocs = purchase?.contract_allocations || [];
  return allocs.some((a) => isTruckLoadContract(a));
}

export function purchaseArrivedAsFarmer(purchase) {
  return Boolean(
    String(purchase?.source_farmer_name || "").trim()
    || purchase?.source_farmer_party_id
    || purchase?.has_source_farmer
  );
}

/** Party weighbridge net must be on file for truck-load, mandi, and a supplier's own supply.
 *  A farmer tractor weighed only at our gate has no party slip, even after a supplier is assigned.
 */
export function purchaseRequiresPartySideWeight(purchase) {
  if (purchaseHasTruckLoadContract(purchase) || purchase?.is_mandi_purchase) return true;
  if (purchaseArrivedAsFarmer(purchase)) return false;
  if (purchase?.requires_party_weight) return true;
  return String(purchase?.party_kind || "").toUpperCase() === "SUPPLIER";
}

/** Party weighbridge net must be on file before truck-load / supplier contract splits. */
export function accountsPartyWeightStatus(purchase) {
  if (!purchaseRequiresPartySideWeight(purchase)) {
    return { required: false, ready: true };
  }
  const party = purchase?.party_net_weight_qtl;
  if (party != null && Number(party) > 0) {
    return { required: true, ready: true, value: Number(party) };
  }
  const supplier = String(purchase?.party_kind || "").toUpperCase() === "SUPPLIER";
  return {
    required: true,
    ready: false,
    message: supplier
      ? "Party side weight is compulsory for supplier supply. Enter it from the party weighbridge slip."
      : "Party side weight was not recorded at weighbridge. Enter it from the party weighbridge slip before linking contracts.",
  };
}

export function buildAccountsReviewSteps(purchase) {
  const steps = [];
  if (accountsPartyWeightStatus(purchase).required) {
    steps.push({ label: "Party weight" });
  }
  steps.push({ label: "Link contracts" }, { label: "Settlement" }, { label: "Finalize" });
  return steps.map((step, index) => ({ id: index + 1, ...step }));
}

export function accountsWorkflowStep(purchase, { splitStatus, hasPreview }) {
  const party = accountsPartyWeightStatus(purchase);
  const offset = party.required ? 1 : 0;
  if (party.required && !party.ready) return 1;
  if (!splitStatus.ready) return 1 + offset;
  if (hasPreview) return 3 + offset;
  return 2 + offset;
}

/** Whether contract net splits are saved and ready for settlement preview / finalize. */
export function accountsContractSplitStatus(purchase) {
  const partyStatus = accountsPartyWeightStatus(purchase);
  if (partyStatus.required && !partyStatus.ready) {
    return { ready: false, step: 1, message: partyStatus.message, needsPartyWeight: true };
  }

  const allocs = purchase?.contract_allocations || [];
  const truckLoad = purchaseHasTruckLoadContract(purchase);
  const truckNet = Number(purchase?.net_weight_qtl);
  const splitTarget = truckLoad && purchase?.party_net_weight_qtl != null && Number(purchase.party_net_weight_qtl) > 0
    ? Number(purchase.party_net_weight_qtl)
    : truckNet;
  const splitLabel = truckLoad ? "party side weight" : "truck net";

  if (!truckNet || truckNet <= 0) {
    return { ready: false, step: 1, message: "Truck net weight must be recorded at weighbridge first." };
  }
  if (truckLoad && (!splitTarget || splitTarget <= 0)) {
    return { ready: false, step: 1, message: partyStatus.message, needsPartyWeight: true };
  }
  if (!allocs.length) {
    return { ready: false, step: 1, message: "Link at least one purchase contract and save." };
  }
  if (allocs.length === 1) {
    const net = allocs[0].net_weight_qtl;
    if (net != null && net !== "" && Number(net) > 0) {
      if (Math.abs(Number(net) - splitTarget) > 0.05) {
        return {
          ready: false,
          step: 1,
          message: truckLoad
            ? `Contract party net (${Number(net).toFixed(2)} qtl) must equal party side weight (${splitTarget.toFixed(2)} qtl). Update and save after removing other contracts.`
            : `Contract net (${Number(net).toFixed(2)} qtl) must equal truck net (${splitTarget.toFixed(2)} qtl). Update net qtl and save after removing other contracts.`,
        };
      }
    }
    return { ready: true, step: 2, message: null };
  }
  const missing = allocs.filter((a) => !a.net_weight_qtl || Number(a.net_weight_qtl) <= 0);
  if (missing.length) {
    return {
      ready: false,
      step: 1,
      message: truckLoad
        ? `Enter and save party net qtl for all ${allocs.length} contracts (${missing.length} still missing).`
        : `Enter and save net qtl for all ${allocs.length} contracts (${missing.length} still missing).`,
    };
  }
  const sum = allocs.reduce((s, a) => s + Number(a.net_weight_qtl || 0), 0);
  if (Math.abs(sum - splitTarget) > 0.05) {
    return {
      ready: false,
      step: 1,
      message: `Contract weight total (${sum.toFixed(2)} qtl) must equal ${splitLabel} (${splitTarget.toFixed(2)} qtl). Save linked contracts.`,
    };
  }
  return { ready: true, step: 2, message: null };
}

/** Supplier mandi purchases need pravesh parchi no. before voucher finalize. */
export function accountsPraveshParchiStatus(purchase) {
  if (purchase?.party_kind !== "SUPPLIER") {
    return { required: false, pending: false, value: "" };
  }
  const value = String(purchase?.pravesh_parchi_no || "").trim();
  return {
    required: true,
    pending: purchase?.pravesh_parchi_pending ?? !value,
    value,
  };
}

/** Material voucher register — supplier trucks missing pravesh parchi on file. */
export function accountsVoucherPraveshParchiStatus(voucher) {
  if (voucher?.party_kind !== "SUPPLIER" || voucher?.voucher_type !== "MATERIAL") {
    return { required: false, pending: false, value: "" };
  }
  const value = String(
    voucher?.pravesh_parchi_no || voucher?.voucher_emandi?.pravesh_parchi_no || "",
  ).trim();
  return {
    required: true,
    pending: voucher?.pravesh_parchi_pending ?? !value,
    value,
  };
}

export function labelFromOptions(options, value) {
  if (!value) return "—";
  const hit = (options || []).find((o) => o.value === value);
  return hit?.label || String(value).replace(/_/g, " ");
}

export function formatQtl(n) {
  return `${Number(n ?? 0).toFixed(2)} qtl`;
}

export function formatInr(n) {
  return `₹${Number(n ?? 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
}

export function pickContractTermsFields(terms = {}) {
  return {
    bardana_deduction: terms.bardana_deduction ?? "",
    cd_applicable: Boolean(terms.cd_applicable),
    cd_percent: terms.cd_percent ?? "",
    broker_applicable: Boolean(terms.broker_applicable),
    brokerage: terms.brokerage ?? "",
    delivery_condition: terms.delivery_condition ?? "",
    freight_rate_per_qtl: terms.freight_rate_per_qtl ?? "",
  };
}

const CONTRACT_TERM_KEYS = [
  "bardana_deduction",
  "cd_applicable",
  "cd_percent",
  "broker_applicable",
  "brokerage",
  "delivery_condition",
  "freight_rate_per_qtl",
];

function normTermValue(key, value) {
  if (key === "cd_applicable" || key === "broker_applicable") return Boolean(value);
  if (value === "" || value == null) return "";
  if (key === "cd_percent" || key === "brokerage" || key === "freight_rate_per_qtl") {
    return Number(value);
  }
  return String(value);
}

export function contractTermsDiffer(active = {}, baseline = {}) {
  return CONTRACT_TERM_KEYS.some(
    (key) => normTermValue(key, active[key]) !== normTermValue(key, baseline[key]),
  );
}

export const PARTY_SETTLEMENT_LINE_CODES = new Set([
  "bardana",
  "cd",
  "brokerage",
  "freight",
  "freight_advice",
  "mandi_weight",
]);

export function extractPartyExpenseLines(alloc) {
  return (alloc?.lines || []).filter(
    (ln) =>
      !PARTY_SETTLEMENT_LINE_CODES.has(ln.code)
      && ln.effect !== "weight"
      && ln.effect !== "info",
  );
}

/** Truck-level party lines from settlement preview (multi-contract safe). */
export function extractPreviewPartyExpenseLines(preview) {
  if (preview?.party_expense_lines?.length) {
    return preview.party_expense_lines;
  }
  return extractPartyExpenseLines(preview?.allocation);
}

function aggregateMultiContractPartyLines(lines = []) {
  const byCode = new Map();
  for (const ln of lines) {
    const code = ln.code || "";
    const amount = Number(ln.amount || 0);
    if (byCode.has(code)) {
      const prev = byCode.get(code);
      prev.amount = roundMoney(prev.amount + amount);
    } else {
      byCode.set(code, { ...ln, amount });
    }
  }
  return [...byCode.values()];
}

/** Party expense lines frozen on a material voucher (overrides dict or snapshot). */
export function extractFrozenPartyExpenseFromDeductions(deductions) {
  if (!deductions) return null;
  const aggregate = Boolean(deductions.multi_contract);
  const overrides = deductions.party_expense_overrides;
  if (overrides && typeof overrides === "object" && !Array.isArray(overrides)) {
    const lines = overrides.lines || [];
    if (lines.length) {
      return {
        lines: aggregate ? aggregateMultiContractPartyLines(lines) : lines,
        frozen: Boolean(deductions.settlement_frozen),
      };
    }
  }
  const snap = deductions.party_expense_snapshot;
  if (snap?.lines?.length) {
    return {
      lines: aggregate ? aggregateMultiContractPartyLines(snap.lines) : snap.lines,
      frozen: Boolean(deductions.settlement_frozen),
    };
  }
  return null;
}

export function formatContractTerms(terms, optionsFor) {
  if (!terms || !Object.keys(terms).length) return [];
  const isMandi = terms.purchase_type === "MANDI_PURCHASE";
  const rows = [
    { label: "Contract", value: terms.contract_number || "—" },
    { label: "Bardana", value: labelFromOptions(optionsFor("bardana"), terms.bardana) },
    ...(isMandi
      ? [{ label: "Weight basis", value: String(terms.weight_basis || "FIXED_PER_BAG").replace(/_/g, " ") }]
      : [{ label: "Bardana deduction", value: labelFromOptions(optionsFor("bardana_deduction"), terms.bardana_deduction) }]),
    { label: "Delivery", value: labelFromOptions(optionsFor("delivery_condition"), terms.delivery_condition) },
    {
      label: "Cash discount (CD)",
      value: terms.cd_applicable ? `${terms.cd_percent}%` : "Not applicable",
    },
    {
      label: "Brokerage",
      value: terms.broker_applicable
        ? `${terms.broker_name || "Broker"} · ${formatInr(terms.brokerage || 0)}`
        : "Not applicable",
    },
  ];
  if (terms.freight_rate_per_qtl != null && terms.freight_rate_per_qtl !== "") {
    rows.push({ label: "Freight rate", value: `${formatInr(terms.freight_rate_per_qtl)}/qtl` });
  }
  return rows;
}

export function lineAmountDisplay(line) {
  if (line.effect === "weight") {
    return `${line.qtl ?? 0} qtl deducted`;
  }
  if (line.effect === "info") {
    return `${formatInr(line.amount || 0)} (advice)`;
  }
  const sign = line.effect === "add" ? "+" : "−";
  return `${sign}${formatInr(line.amount || 0)}`;
}

function baselineBillableQtl(alloc, terms) {
  const scoped = Number(alloc.scoped_net_qtl ?? alloc.net_qtl ?? 0);
  const bardanaCode = terms?.bardana_deduction;
  let bardana = Number(alloc.bardana_deduction_qtl || 0);
  if (terms && bardanaCode) {
    const factors = { GM_500_PER_QTL: 0.005, GM_300_PER_QTL: 0.003, KG_1_PER_QTL: 0.01 };
    bardana = Math.round(scoped * (factors[bardanaCode] || 0) * 1000) / 1000;
  }
  return Math.round(Math.max(0, scoped - bardana) * 100) / 100;
}

function baselineGross(alloc, terms) {
  const rate = Number(alloc.contract_rate_per_qtl ?? alloc.rate_per_qtl ?? 0);
  return Math.round(baselineBillableQtl(alloc, terms) * rate * 100) / 100;
}

function formatBaselineTermsValue(key, baseline, alloc, optionsFor) {
  if (!baseline) return "—";
  if (key === "bardana") {
    const q = baselineBillableQtl(alloc, baseline);
    const net = Number(alloc.scoped_net_qtl ?? alloc.net_qtl ?? 0);
    const bardana = Math.round((net - q) * 1000) / 1000;
    return bardana > 0
      ? `${labelFromOptions(optionsFor("bardana_deduction"), baseline.bardana_deduction)} · ${formatQtl(bardana)}`
      : labelFromOptions(optionsFor("bardana_deduction"), baseline.bardana_deduction) || "None";
  }
  if (key === "cd") {
    if (!baseline.cd_applicable) return "Not applicable";
    const amt = Math.round(baselineGross(alloc, baseline) * Number(baseline.cd_percent || 0) / 100 * 100) / 100;
    return `−${formatInr(amt)} (${baseline.cd_percent}%)`;
  }
  if (key === "brokerage") {
    if (!baseline.broker_applicable) return "Not applicable";
    return `−${formatInr(baseline.brokerage || 0)}`;
  }
  if (key === "freight") {
    const rate = baseline.freight_rate_per_qtl;
    const net = Number(alloc.scoped_net_qtl ?? alloc.net_qtl ?? 0);
    const delivery = labelFromOptions(optionsFor("delivery_condition"), baseline.delivery_condition);
    if (rate && net) {
      const amt = Math.round(net * Number(rate) * 100) / 100;
      return `${delivery} · ${formatInr(amt)}`;
    }
    return delivery || "—";
  }
  return "—";
}

function findLine(alloc, code) {
  return (alloc.lines || []).find((l) => l.code === code);
}

/** Rates from linked contract allocations (step 2), not legacy negotiated_rate. */
export function ratesFromPurchaseAllocations(purchase) {
  const allocs = purchase?.contract_allocations || [];
  const allocation_rates = {};
  for (const a of allocs) {
    if (a.rate_per_qtl != null && a.rate_per_qtl !== "") {
      allocation_rates[String(a.purchase_contract_id)] = String(a.rate_per_qtl);
    }
  }
  const multi = allocs.length > 1;
  const revised_rate =
    !multi && allocs[0]?.rate_per_qtl != null && allocs[0]?.rate_per_qtl !== ""
      ? String(allocs[0].rate_per_qtl)
      : "";
  return { allocation_rates, revised_rate };
}

/** Stable key for purchase contract allocation changes (save, remove, net edit). */
export function purchaseAllocKey(purchase) {
  return JSON.stringify(
    (purchase?.contract_allocations || []).map((a) => ({
      id: a.purchase_contract_id,
      rate: a.rate_per_qtl,
      net: a.net_weight_qtl,
      bags: a.bags_billable ?? a.bags_settlement?.bags_billable ?? a.party_invoice_bags,
      invoice: a.party_invoice_bags,
      unloaded: a.bags_unloaded,
    })),
  );
}

/** Drop settlement form fields for contracts no longer linked on this truck. */
export function pruneFormForPurchase(purchase, form) {
  if (!form) return form;
  const ids = new Set((purchase?.contract_allocations || []).map((a) => String(a.purchase_contract_id)));
  const allocation_rates = {};
  for (const [id, v] of Object.entries(form.allocation_rates || {})) {
    if (ids.has(id)) allocation_rates[id] = v;
  }
  const contract_terms = {};
  const contract_terms_baseline = {};
  for (const id of ids) {
    if (form.contract_terms?.[id]) contract_terms[id] = form.contract_terms[id];
    if (form.contract_terms_baseline?.[id]) contract_terms_baseline[id] = form.contract_terms_baseline[id];
  }
  return { ...form, allocation_rates, contract_terms, contract_terms_baseline };
}

/** Build API payload for contract term overrides from form state (only changed fields). */
export function buildContractTermOverridesPayload(form, purchase = null) {
  const pruned = purchase ? pruneFormForPurchase(purchase, form) : form;
  const terms = pruned?.contract_terms || {};
  const baseline = pruned?.contract_terms_baseline || {};
  return Object.entries(terms)
    .map(([id, t]) => {
      const base = baseline[id] || {};
      if (!contractTermsDiffer(t, base)) return null;
      const row = { purchase_contract_id: Number(id) };
      if (t.bardana_deduction && t.bardana_deduction !== base.bardana_deduction) {
        row.bardana_deduction = t.bardana_deduction;
      }
      if (Boolean(t.cd_applicable) !== Boolean(base.cd_applicable)) {
        row.cd_applicable = Boolean(t.cd_applicable);
      }
      if (normTermValue("cd_percent", t.cd_percent) !== normTermValue("cd_percent", base.cd_percent)) {
        if (t.cd_percent !== "" && t.cd_percent != null) row.cd_percent = Number(t.cd_percent);
      }
      if (Boolean(t.broker_applicable) !== Boolean(base.broker_applicable)) {
        row.broker_applicable = Boolean(t.broker_applicable);
      }
      if (normTermValue("brokerage", t.brokerage) !== normTermValue("brokerage", base.brokerage)) {
        if (t.brokerage !== "" && t.brokerage != null) row.brokerage = Number(t.brokerage);
      }
      if (t.delivery_condition && t.delivery_condition !== base.delivery_condition) {
        row.delivery_condition = t.delivery_condition;
      }
      if (
        normTermValue("freight_rate_per_qtl", t.freight_rate_per_qtl)
        !== normTermValue("freight_rate_per_qtl", base.freight_rate_per_qtl)
      ) {
        if (t.freight_rate_per_qtl !== "" && t.freight_rate_per_qtl != null) {
          row.freight_rate_per_qtl = Number(t.freight_rate_per_qtl);
        }
      }
      return Object.keys(row).length > 1 ? row : null;
    })
    .filter(Boolean);
}

/** Truck-wide rows. scope: 'all' | 'material' | 'freight' */
export function buildTruckReviewRows(form, { isMandi = false, scope = "all" } = {}) {
  const showMaterial = scope === "all" || scope === "material";
  const showFreight = scope === "all" || scope === "freight";
  const rows = [];
  if (showMaterial) {
    rows.push({
      key: "deduction_scope",
      particular: "Deduction quantity basis",
      condition: null,
      conditionEdit: { field: "deduction_scope", type: "select" },
      contractValue: form?.deduction_scope === "REMAINING" ? "Remaining quantity only" : "Full truck quantity",
      edit: null,
      final:
        form?.deduction_scope === "REMAINING"
          ? "Deductions on remaining qtl only"
          : "Deductions on complete net qtl",
    });
  }
  if (showFreight) {
    rows.push(
      {
        key: "freight_rate",
        particular: "Freight rate",
        condition: null,
        conditionEdit: null,
        contractValue: "Per contract terms",
        edit: { field: "freight_rate_per_qtl", type: "number", step: "0.01", min: 0, placeholder: "₹/qtl" },
        final:
          form?.freight_rate_per_qtl !== "" && form?.freight_rate_per_qtl != null
            ? `${formatInr(form.freight_rate_per_qtl)}/qtl applied`
            : "Contract freight rate",
      },
      {
        key: "freight_amount",
        particular: "Freight total override",
        condition: null,
        conditionEdit: null,
        contractValue: "Computed from rate × net qtl",
        edit: { field: "freight_amount", type: "number", step: "0.01", min: 0, placeholder: "Total ₹" },
        final:
          form?.freight_amount !== "" && form?.freight_amount != null
            ? formatInr(form.freight_amount)
            : "From contract / rate",
      },
    );
  }
  if (showFreight && isMandi && scope !== "freight") {
    rows.unshift(
      {
        key: "party_net",
        particular: "Party weighbridge net (qtl)",
        condition: "Freight settlement load weight",
        conditionEdit: null,
        contractValue: "From party gate pass / slip (step 1)",
        edit: { field: "party_net_weight_qtl", type: "number", step: "0.01", min: 0, placeholder: "e.g. 305" },
        final:
          form?.party_net_weight_qtl !== "" && form?.party_net_weight_qtl != null
            ? formatQtl(form.party_net_weight_qtl)
            : "Enter in step 1 or here",
        highlight: true,
      },
    );
  }
  return rows;
}

/**
 * Per-contract settlement review rows.
 * Contract condition column is editable; Value as per contract shows registered contract baseline.
 */
export function buildSettlementReviewRows(alloc, form, optionsFor, { multiContract = false, isMandi = false } = {}) {
  const cid = String(alloc.purchase_contract_id);
  const baseline = form?.contract_terms_baseline?.[cid] || alloc.original_contract_terms || alloc.contract_terms || {};
  const active = form?.contract_terms?.[cid] || pickContractTermsFields(baseline);
  const contractRate = alloc.contract_rate_per_qtl ?? alloc.rate_per_qtl ?? 0;
  const mandiWeight = alloc.mandi_weight || alloc.deductions?.mandi_weight || {};
  const delivery = String(
    baseline.delivery_condition || alloc.contract_terms?.delivery_condition || "",
  ).toUpperCase();
  const isFor = !isMandi && delivery === "FOR";
  const forWeight = alloc.for_weight || alloc.deductions?.for_weight || null;
  const rows = [];

  rows.push({
    key: "net_qtl",
    particular: isMandi ? "Our weighbridge net" : isFor ? "Material weight" : "Net weight",
    condition: isMandi
      ? "For freight comparison only"
      : isFor
        ? "Lower of party side and our weighbridge"
        : "Weighbridge (gross − tare)",
    conditionEdit: null,
    contractValue:
      isFor && forWeight && Number(forWeight.party_net_qtl) > 0 && Number(forWeight.our_net_qtl) > 0
        ? `Party ${formatQtl(forWeight.party_net_qtl)} · Ours ${formatQtl(forWeight.our_net_qtl)}`
        : formatQtl(alloc.net_qtl),
    edit: null,
    final: formatQtl(alloc.net_qtl),
  });

  if (isMandi) {
    rows.push({
      key: "mandi_bardana",
      particular: "Party bardana (from party chart)",
      condition: `${mandiWeight.bardana_kg_per_bag ?? "—"} kg/bag`,
      conditionEdit: null,
      contractValue: `${formatQtl(mandiWeight.threshold_qtl || 0)} threshold`,
      edit: null,
      final: formatQtl(alloc.bardana_deduction_qtl || mandiWeight.bardana_qtl || 0),
    });
  } else {
    rows.push({
      key: "bardana",
      particular: "Bardana weight deduction",
      condition: null,
      conditionEdit: { type: "bardana_deduction", contractId: alloc.purchase_contract_id },
      contractValue: formatBaselineTermsValue("bardana", baseline, alloc, optionsFor),
      edit: null,
      final: formatQtl(alloc.bardana_deduction_qtl || 0),
    });
  }

  rows.push({
    key: "weight_cut",
    particular: "Weight cut",
    condition: "Quality / negotiation on billable qtl",
    conditionEdit: null,
    contractValue: "0%",
    edit: { field: "weight_cut_pct", type: "number", step: "0.1", min: 0 },
    final: `${Number(alloc.weight_cut_pct || 0)}% → ${formatQtl(alloc.billable_qtl)} billable`,
  });

  rows.push({
    key: "rate",
    particular: "Rate",
    condition: `Registered contract · ${alloc.variety || ""}`.trim(),
    conditionEdit: null,
    contractValue: `${formatInr(contractRate)}/qtl`,
    edit: multiContract
      ? { field: "allocation_rates", type: "number", contractId: alloc.purchase_contract_id, step: "0.01", min: 0 }
      : { field: "revised_rate", type: "number", step: "0.01", min: 0 },
    final: alloc.effective_landed_rate
      ? `${formatInr(alloc.effective_rate)}/qtl base → ${formatInr(alloc.effective_landed_rate)}/qtl landed`
      : `${formatInr(alloc.effective_rate)}/qtl effective`,
  });

  rows.push({
    key: "price_cut",
    particular: "Price cut",
    condition: "Negotiated reduction on rate",
    conditionEdit: null,
    contractValue: "0%",
    edit: { field: "price_cut_pct", type: "number", step: "0.1", min: 0 },
    final: `${Number(alloc.price_cut_pct || 0)}%`,
  });

  rows.push({
    key: "gross",
    particular: "Gross amount",
    condition: "Billable qtl × effective rate",
    conditionEdit: null,
    contractValue: formatInr(baselineGross(alloc, baseline)),
    edit: null,
    final: formatInr(alloc.gross_amount),
    highlight: true,
  });

  rows.push({
    key: "cd",
    particular: "Cash discount (CD)",
    condition: null,
    conditionEdit: { type: "cd", contractId: alloc.purchase_contract_id },
    contractValue: formatBaselineTermsValue("cd", baseline, alloc, optionsFor),
    edit: null,
    final: findLine(alloc, "cd") ? lineAmountDisplay(findLine(alloc, "cd")) : active.cd_applicable ? "—" : "N/A",
  });

  rows.push({
    key: "brokerage",
    particular: "Brokerage",
    condition: null,
    conditionEdit: { type: "brokerage", contractId: alloc.purchase_contract_id },
    contractValue: formatBaselineTermsValue("brokerage", baseline, alloc, optionsFor),
    edit: null,
    final: findLine(alloc, "brokerage") ? lineAmountDisplay(findLine(alloc, "brokerage")) : active.broker_applicable ? "—" : "N/A",
  });

  rows.push({
    key: "freight",
    particular: "Freight / delivery",
    condition: null,
    conditionEdit: { type: "freight", contractId: alloc.purchase_contract_id },
    contractValue: formatBaselineTermsValue("freight", baseline, alloc, optionsFor),
    edit: null,
    final: (() => {
      const fl = findLine(alloc, "freight") || findLine(alloc, "freight_advice");
      return fl ? lineAmountDisplay(fl) : "—";
    })(),
  });

  for (const line of alloc.lines || []) {
    if (["bardana", "cd", "brokerage", "freight", "freight_advice"].includes(line.code)) continue;
    if (multiContract) continue;
    rows.push({
      key: `line-${line.code}`,
      particular: line.label,
      condition: "Party expense rules",
      conditionEdit: null,
      contractValue: "Per party master",
      edit: null,
      final: lineAmountDisplay(line),
    });
  }

  const contractNet = multiContract
    ? Number(alloc.after_contract_gross ?? alloc.net_payable ?? 0)
    : Number(alloc.net_payable ?? 0);

  rows.push({
    key: "net_payable",
    particular: multiContract ? "Net payable (this contract)" : "Net payable",
    condition: multiContract
      ? "Gross − contract deductions (truck party expenses below)"
      : "Gross − deductions + additions",
    conditionEdit: null,
    contractValue: "—",
    edit: null,
    final: formatInr(contractNet),
    highlight: true,
    total: true,
  });

  return rows;
}

function roundMoney(n) {
  return Math.round(Number(n || 0) * 100) / 100;
}

/** Party freight bill adjustment from freight_payment or stored ledger snapshot. */
export function freightPartyLedgerFromPayment(payment) {
  if (!payment) return null;
  const ledger = payment.party_ledger || {};
  const amount = Number(ledger.party_bill_amount ?? payment.party_bill_amount ?? 0);
  const effect = ledger.party_bill_effect ?? payment.party_bill_effect;
  if (amount <= 0 || !effect) return null;
  return {
    amount: roundMoney(amount),
    effect,
    label: ledger.party_bill_label ?? payment.party_bill_label ?? "Party freight",
  };
}

/** Inverse of backend _material_net_after_freight_party_ledger — base material before party freight line. */
export function materialNetBeforeFreightPartyLedger(storedNet, ledgerOrPayment) {
  const net = Number(storedNet || 0);
  const ledger = ledgerOrPayment?.amount != null && ledgerOrPayment?.effect
    ? ledgerOrPayment
    : freightPartyLedgerFromPayment(ledgerOrPayment);
  if (!ledger) return roundMoney(net);
  if (ledger.effect === "add") return roundMoney(net - ledger.amount);
  if (ledger.effect === "subtract") return roundMoney(net + ledger.amount);
  return roundMoney(net);
}

/** Resolve party freight ledger from freight voucher and/or material deductions. */
export function resolveFreightPartyLedger(material, freight) {
  const fromFreight = freightPartyLedgerFromPayment(freight?.freight_payment);
  if (fromFreight) return fromFreight;

  const deductions = material?.deductions;
  if (!deductions) return null;

  const stored = deductions.freight_party_ledger;
  if (stored) {
    const line = freightPartyLedgerFromPayment({ party_ledger: stored, ...stored });
    if (line) return line;
  }

  const lineEntry = (deductions.lines || []).find((ln) => ln.code === "freight_party_ledger");
  if (lineEntry && Number(lineEntry.amount || 0) > 0) {
    return {
      amount: roundMoney(lineEntry.amount),
      effect: lineEntry.effect,
      label: lineEntry.label || "Party freight",
    };
  }
  return null;
}

/**
 * Split unified purchase voucher amounts for register/detail UI.
 * Material base excludes party freight advance; transporter freight is separate.
 */
export function buildPurchaseVoucherAmountSummary({ material = null, freight = null, preview = null } = {}) {
  const ledger = resolveFreightPartyLedger(material, freight)
    || (preview ? freightPartyLedgerFromPayment(preview.freight_payment) : null);

  const storedMaterialNet = material
    ? Number(material.net_payable || 0)
    : preview?.material_net_payable != null
      ? Number(preview.material_net_payable)
      : preview?.allocations?.length
        ? roundMoney(preview.allocations.reduce((sum, a) => sum + Number(a.net_payable || 0), 0))
        : null;

  const materialBase = storedMaterialNet != null
    ? materialNetBeforeFreightPartyLedger(storedMaterialNet, ledger)
    : null;

  const transporterFreight = freight
    ? {
        amount: Number(freight.net_payable || 0),
        draft: freight.freight_status !== "FINALIZED",
      }
    : preview?.freight_settlement
      ? {
          amount: Number(preview.freight_settlement.net_freight_payable || 0),
          draft: false,
        }
      : null;

  return {
    materialBase,
    materialGross: material?.gross_amount ?? null,
    partyFreight: ledger,
    transporterFreight,
  };
}

/** Compact label for party freight line on register cards. */
export function partyFreightDisplayLabel(ledger) {
  if (!ledger) return "";
  const short = ledger.effect === "add" ? "Party freight" : "Party freight (bill)";
  return ledger.label?.includes("advance") ? "Party freight advance" : short;
}

/**
 * Merge legacy per-contract MATERIAL vouchers (same purchase) into one display row.
 * Keeper is the lowest voucher id — matches backend _consolidate_purchase_material_vouchers.
 */
export function mergeMaterialVouchers(materials = []) {
  const rows = (materials || []).filter((v) => v?.voucher_type === "MATERIAL" && v?.id != null);
  if (!rows.length) return null;
  if (rows.length === 1) {
    const single = rows[0];
    const legacyCodes = single.legacy_merged_voucher_codes || single.legacyMergedCodes;
    if (legacyCodes?.length > 1) {
      return {
        ...single,
        legacyMerged: true,
        legacyMergedIds: single.legacy_merged_voucher_ids || single.legacyMergedIds || [single.id],
        legacyMergedCodes: legacyCodes,
      };
    }
    return single;
  }

  const sorted = [...rows].sort((a, b) => Number(a.id) - Number(b.id));
  const keeper = { ...sorted[0] };
  const legacyMergedIds = sorted.map((m) => m.id);
  const legacyMergedCodes = sorted.map((m) => m.code).filter(Boolean);
  const alreadyCombined = Boolean(keeper.multi_contract || keeper.deductions?.multi_contract);

  if (alreadyCombined) {
    return {
      ...keeper,
      legacyMerged: sorted.length > 1,
      legacyMergedIds,
      legacyMergedCodes,
    };
  }

  const labels = [];
  for (const row of sorted) {
    const label = String(row.contract_label || "").trim();
    if (label && !labels.includes(label)) labels.push(label);
  }

  return {
    ...keeper,
    contract_label: labels.length ? labels.join(" + ") : keeper.contract_label,
    net_payable: roundMoney(sorted.reduce((sum, row) => sum + Number(row.net_payable || 0), 0)),
    gross_amount: roundMoney(sorted.reduce((sum, row) => sum + Number(row.gross_amount || 0), 0)),
    net_qtl: roundMoney(sorted.reduce((sum, row) => sum + Number(row.net_qtl || 0), 0)),
    billable_qtl: roundMoney(sorted.reduce((sum, row) => sum + Number(row.billable_qtl || 0), 0)),
    multi_contract: sorted.length > 1,
    legacyMerged: true,
    legacyMergedIds,
    legacyMergedCodes,
    settlement_frozen: sorted.every(
      (row) => row.settlement_frozen || row.deductions?.settlement_frozen,
    ),
    gl_posted: sorted.every((row) => row.gl_posted),
  };
}

/** Whether a grouped register row includes this voucher id (keeper, freight, payment, or legacy merge). */
export function voucherGroupMatchesVoucherId(group, voucherId) {
  if (!group || voucherId == null) return false;
  const id = Number(voucherId);
  if (Number(group.primaryId) === id) return true;
  if (Number(group.material?.id) === id) return true;
  if (Number(group.freight?.id) === id) return true;
  if ((group.material?.legacyMergedIds || []).some((mid) => Number(mid) === id)) return true;
  if ((group.material?.legacy_merged_voucher_ids || []).some((mid) => Number(mid) === id)) return true;
  if ((group.payments || []).some((row) => Number(row.id) === id)) return true;
  return (group.extras || []).some((row) => Number(row.id) === id);
}

function _cashPaymentMode(voucher) {
  const mode = String(voucher?.payment_mode || voucher?.deductions?.payment_mode || "").toUpperCase();
  if (mode === "BANK") return "BANK";
  const code = String(voucher?.code || "");
  if (code.startsWith("BP-") || code.startsWith("BR-")) return "BANK";
  return "CASH";
}

/**
 * Printable voucher rows for one purchase family (single + pack printing).
 */
export function purchaseRegisterPrintItems(group) {
  const items = [];
  const push = (row, label, short) => {
    if (!row?.id) return;
    items.push({
      id: Number(row.id),
      code: row.code || `#${row.id}`,
      voucher_type: row.voucher_type,
      label,
      short,
      payment_mode: _cashPaymentMode(row),
    });
  };
  if (group?.material) push(group.material, "Purchase", "Mat");
  if (group?.freight) push(group.freight, "Freight", "Frt");
  for (const row of group?.extras || []) {
    if (row?.voucher_type === "TDS") push(row, "TDS", "TDS");
    else if (row?.id) push(row, row.voucher_type || "Voucher", "Doc");
  }
  for (const row of group?.payments || []) {
    const bank = _cashPaymentMode(row) === "BANK";
    push(row, bank ? "Bank payment" : "Cash", bank ? "BP" : "CSH");
  }
  return items;
}

/**
 * Related voucher chips for one PT- register line (Purchase · Freight · TDS · Cash/Bank payment).
 */
export function purchaseRegisterRelatedParts(group) {
  const parts = [];
  const material = group?.material;
  if (material?.legacyMergedCodes?.length > 1) {
    for (const code of material.legacyMergedCodes) {
      parts.push({ type: "MATERIAL", code, label: "Purchase" });
    }
  } else if (material?.code) {
    parts.push({ type: "MATERIAL", code: material.code, label: "Purchase" });
  }
  if (group?.freight?.code) {
    parts.push({ type: "FREIGHT", code: group.freight.code, label: "Freight" });
  }
  for (const row of group?.extras || []) {
    if (!row?.code) continue;
    if (row.voucher_type === "TDS") {
      parts.push({ type: "TDS", code: row.code, label: "TDS" });
    } else {
      parts.push({ type: row.voucher_type || "OTHER", code: row.code, label: row.voucher_type || "Voucher" });
    }
  }
  for (const row of group?.payments || []) {
    if (!row?.code) continue;
    const bank = _cashPaymentMode(row) === "BANK";
    parts.push({
      type: "CASH",
      code: row.code,
      label: bank ? "Bank payment" : "Cash",
      payment_mode: bank ? "BANK" : "CASH",
    });
  }
  return parts;
}

/**
 * One register row per purchase (PT-) — MATERIAL + FREIGHT + TDS + linked CASH/BP.
 * Unlinked / on-account cash is omitted (Cash ledger tab).
 */
export function groupVouchersByPurchase(vouchers = []) {
  const byPurchase = new Map();
  const PURCHASE_REGISTER_TYPES = new Set(["MATERIAL", "FREIGHT", "TDS", "CASH"]);

  for (const voucher of vouchers) {
    if (!voucher || !PURCHASE_REGISTER_TYPES.has(voucher.voucher_type)) continue;
    if (!voucher.purchase_id) continue;
    const purchaseId = voucher.purchase_id;
    if (!byPurchase.has(purchaseId)) {
      byPurchase.set(purchaseId, {
        purchase_id: purchaseId,
        materials: [],
        freight: null,
        extras: [],
        payments: [],
      });
    }
    const bucket = byPurchase.get(purchaseId);
    if (voucher.voucher_type === "MATERIAL") bucket.materials.push(voucher);
    else if (voucher.voucher_type === "FREIGHT") bucket.freight = voucher;
    else if (voucher.voucher_type === "CASH") bucket.payments.push(voucher);
    else bucket.extras.push(voucher); // TDS (+ any other purchase-linked extras)
  }

  const buildPurchaseGroup = (purchaseId, bucket) => {
    const material = mergeMaterialVouchers(bucket.materials);
    const primary = material || bucket.freight || bucket.extras[0] || bucket.payments[0] || {};
    return {
      key: `purchase-${purchaseId}`,
      purchase_id: purchaseId,
      purchase_code: primary.purchase_code,
      vehicle_number: primary.vehicle_number,
      party_name: primary.party_name,
      gate_pass_code: primary.gate_pass_code,
      contract_label: material?.contract_label || primary.contract_label,
      invoice_no: primary.invoice_no,
      form_9r_no: primary.form_9r_no,
      pravesh_parchi_no: primary.pravesh_parchi_no,
      accounts_reopen: material?.accounts_reopen || bucket.freight?.accounts_reopen || null,
      material,
      freight: bucket.freight,
      extras: bucket.extras,
      payments: bucket.payments,
      primaryId:
        material?.id
        || bucket.freight?.id
        || bucket.extras[0]?.id
        || bucket.payments[0]?.id,
    };
  };

  const emittedPurchases = new Set();
  const grouped = [];

  for (const voucher of vouchers) {
    if (!voucher || !PURCHASE_REGISTER_TYPES.has(voucher.voucher_type)) continue;
    if (!voucher.purchase_id) continue;
    const purchaseId = voucher.purchase_id;
    if (emittedPurchases.has(purchaseId)) continue;
    emittedPurchases.add(purchaseId);
    grouped.push(buildPurchaseGroup(purchaseId, byPurchase.get(purchaseId)));
  }

  return grouped;
}
